import type {
  IndicationDetailCardGroup,
  IndicationDetailCardLeaf,
} from 'features/indicationTriage'
import { FfsRegionClassification } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionClassificationSchema'
import { FfsPart } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'

type FfsDetailCard = IndicationDetailCardLeaf | IndicationDetailCardGroup

/**
 * Config for the shared `IndicationOverview` card when used for a libra FFS LTA.
 * Auto-generated LTAs carry no backend `data.detail`, so hide the report block
 * (leaving just the thickness histogram) and let the histogram fill the card.
 */
const LTA_OVERVIEW_CONFIG = {
  hide_detail: true,
  full_width_histogram: true,
} as const

/**
 * Canonical detail-card stack for libra FFS LTA indications -- the single
 * source of truth used both when generating a new indication set
 * (`buildLocalThinAreaIndicationSetEntry`) and as the fallback in the triage
 * step when a set has no persisted `detailCards`.
 *
 *  - `IndicationOverview` (LTA-configured) -- thickness histogram only.
 *  - `Ruler` -- distance measurements ("Distances").
 *  - `CriticalProfile` -- critical thickness profile chart ("Critical Paths").
 *  - `FfsCalc` -- Part 4 / Part 5 FFS calculations.
 *  - `Note` -- engineer free-text notes (editable).
 */
export const DEFAULT_FFS_LTA_DETAIL_CARDS: ReadonlyArray<
  IndicationDetailCardLeaf | IndicationDetailCardGroup
> = [
  {
    id: 'overview',
    component: 'IndicationOverview',
    config: LTA_OVERVIEW_CONFIG,
  },
  { id: 'ruler', component: 'Ruler' },
  { id: 'criticalProfile', component: 'CriticalProfile' },
  { id: 'ffsCalc', component: 'FfsCalc' },
  { id: 'note', component: 'Note' },
]

/**
 * Upgrades a persisted FFS LTA detail-card config for display. Indication sets
 * generated before this change persist a bare `IndicationOverview` leaf (its
 * report block is empty for auto-generated LTAs) and may omit the `Note` card;
 * this merges the LTA overview config onto the `IndicationOverview` leaf (so it
 * renders the histogram only, full width) and appends `Note` if absent, so no
 * data migration is required. New sets built from `DEFAULT_FFS_LTA_DETAIL_CARDS`
 * already carry the config, so the merge is idempotent for them.
 */
export function normalizeFfsDetailCards(
  cards: ReadonlyArray<IndicationDetailCardLeaf | IndicationDetailCardGroup>
): ReadonlyArray<IndicationDetailCardLeaf | IndicationDetailCardGroup> {
  const mapped = cards.map((card) =>
    'component' in card && card.component === 'IndicationOverview'
      ? { ...card, config: { ...LTA_OVERVIEW_CONFIG, ...(card.config ?? {}) } }
      : card
  )
  const hasNote = mapped.some(
    (card) => 'component' in card && card.component === 'Note'
  )
  return hasNote ? mapped : [...mapped, { id: 'note', component: 'Note' }]
}

/**
 * Pins every `FfsCalc` leaf in a detail-card tree to a single FFS part by
 * overlaying `config.ffsPart`, so the calc card hides its Part selector and
 * renders LOCKED Level tabs (see `FfsCalcCard`). Used by the part-scoped FFS
 * steps (Part 4 / Part 5).
 *
 * Recurses into `IndicationDetailCardGroup.children` -- the `FfsCalc` leaf is
 * not guaranteed to be top-level. Applies to whatever cards the caller passes,
 * i.e. BOTH the canonical defaults AND any persisted `ltaSet.detailCards`
 * (which carry a bare `FfsCalc` leaf), so no data migration is required.
 * The existing `config` is preserved and `ffsPart` merged on top.
 *
 * `classification` (the selected LTA's committed region classification) is
 * injected the same way when provided -- the calc card can't take an arbitrary
 * prop (detail cards are rendered generically with only `config`), so the
 * decision rides the config channel and the card reads `config.classification`.
 */
export function normalizeFfsDetailCardsForPart(
  cards: ReadonlyArray<FfsDetailCard>,
  ffsPart: FfsPart,
  classification?: FfsRegionClassification | null
): ReadonlyArray<FfsDetailCard> {
  const pinCard = (card: FfsDetailCard): FfsDetailCard => {
    if ('component' in card) {
      return card.component === 'FfsCalc'
        ? {
            ...card,
            config: {
              ...(card.config ?? {}),
              ffsPart,
              // Set unconditionally, including when null: an explicit `null`
              // tells the pinned calc card "this LTA has no classification" (so it
              // shows the non-blocking Pre-Planning warning and runs permissively),
              // whereas a missing key reads as the unpinned FDE path. Don't launder
              // null into undefined.
              classification: classification ?? null,
            },
          }
        : card
    }
    return { ...card, children: card.children.map(pinCard) }
  }
  return cards.map(pinCard)
}
